<template>
  <div class="border-2 px-3 py-4 radius-20 border-spect-gray overflow-x-auto min-w-[50rem] mb-6">
    <DataTable
      :value="campaigns"
      :stripedRows="true"
      :paginator="campaigns.length > 10"
      :rows="10"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="$t('common.pagination_template')"
      class="p-datatable-sm min-w-[50rem]"
      :emptyMessage="$t('statistics.empty_campaigns')"
    >
      <Column field="name" :header="$t('statistics.campaign_name')" sortable>
        <template #body="{ data }">
          <span class="font-medium">{{ data.name }}</span>
        </template>
      </Column>

      <Column field="status" :header="$t('statistics.campaign_status')" sortable>
        <template #body="{ data }">
          <Tag
            :value="formatStatus(data.status)"
            :severity="getStatusSeverity(data.status)"
          />
        </template>
      </Column>

      <Column
        v-if="type === 'micro'"
        field="campaign_type"
        :header="$t('statistics.campaign_type')"
        sortable
      >
        <template #body="{ data }">
          <span class="capitalize">{{ data.campaign_type || "-" }}</span>
        </template>
      </Column>

      <Column
        v-if="type !== 'micro'"
        field="total"
        :header="$t('statistics.total')"
        sortable
      >
        <template #body="{ data }">{{ data.total ?? 0 }}</template>
      </Column>

      <Column
        :field="type === 'micro' ? 'total_sent' : 'sent'"
        :header="$t('statistics.sent')"
        sortable
      >
        <template #body="{ data }">
          {{ type === "micro" ? data.total_sent ?? 0 : data.sent ?? 0 }}
        </template>
      </Column>

      <Column field="opened" :header="$t('statistics.opened')" sortable>
        <template #body="{ data }">{{ data.opened ?? 0 }}</template>
      </Column>

      <Column field="clicked" :header="$t('statistics.clicked')" sortable>
        <template #body="{ data }">
          <span :class="{ 'text-red-500 font-semibold': data.clicked > 0 }">
            {{ data.clicked ?? 0 }}
          </span>
        </template>
      </Column>

      <Column
        v-if="type === 'phishing'"
        field="reported"
        :header="$t('statistics.reported')"
        sortable
      >
        <template #body="{ data }">
          <span :class="{ 'text-green-600 font-semibold': data.reported > 0 }">
            {{ data.reported ?? 0 }}
          </span>
        </template>
      </Column>

      <Column field="open_rate" :header="$t('statistics.open_rate')" sortable>
        <template #body="{ data }">
          {{ formatRate(data.open_rate) }}
        </template>
      </Column>

      <Column field="click_rate" :header="$t('statistics.click_rate')" sortable>
        <template #body="{ data }">
          {{ formatRate(data.click_rate) }}
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup>
import { defineProps } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tag from "primevue/tag";

defineProps({
  campaigns: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    required: true,
    validator: (v) => ["micro", "phishing", "smishing"].includes(v),
  },
});

const formatRate = (value) => {
  const n = Number(value) || 0;
  return `${n}%`;
};

const formatStatus = (status) => {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getStatusSeverity = (status) => {
  if (!status) return "info";
  const s = status.toLowerCase();
  if (s === "completed") return "success";
  if (s === "created" || s === "queued") return "info";
  if (s === "in_progress" || s === "processing") return "warning";
  if (s === "failed" || s === "abandoned") return "danger";
  if (s === "closed") return "secondary";
  return "info";
};
</script>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss" as *;
@use "../../assets/scss/app/common.scss" as *;
</style>
